import React, { useState, useMemo, useCallback, useRef } from 'react'
import { flushSync } from 'react-dom'
import { Link, useNavigate } from 'react-router-dom'
import { Region, PlacementSummary } from '../../services/api'
import WidgetTypeBadge from './WidgetTypeBadge'
import { getPlacementStatus, getScheduleDisplay } from '../../utils/merchandising'
import { convertUTCToPTInput } from '../../utils/timezone'
import { Badge, Panel } from '../../components'
import KebabMenu from '../KebabMenu'
import { arrayMove } from '../../utils/arrayMove'
import rowStyles from '../../pages/merchandising/PlacementRows.module.scss'
// NOTE: RegionPanel reorders position-groups with pinned-row semantics, not a
// plain flat list, so it keeps its own drag handlers rather than useDragReorder.

interface PositionGroup {
  position: number
  placements: PlacementSummary[]
  isPinned: boolean
}


const RegionPanel: React.FC<{
  region: Region
  placementsByPosition: Record<number, PlacementSummary[]>
  buildAddPlacementLink: (regionId: string, sortOrder: number) => string
  /** Path to the placement drawer for a placement id, or 'new' plus context. */
  placementPath: (placementId: string, params?: URLSearchParams) => string
  onPin: (placements: PlacementSummary[]) => void
  onReorder: (regionId: string, items: Array<{ placement_id: string; sort_order: number }>) => void
  onDuplicate: (p: PlacementSummary) => void
  onToggleActive: (p: PlacementSummary) => void
  onDelete: (p: PlacementSummary) => void
}> = ({ region, placementsByPosition, buildAddPlacementLink, placementPath, onPin, onReorder, onDuplicate, onToggleActive, onDelete }) => {
  const navigate = useNavigate()

  // Build position groups from the data
  const baseGroups: PositionGroup[] = useMemo(() => {
    return Object.keys(placementsByPosition)
      .map(Number)
      .sort((a, b) => a - b)
      .map(pos => {
        const pls = placementsByPosition[pos]
        return {
          position: pos,
          placements: pls,
          isPinned: pls.some(p => p.pinned_position != null),
        }
      })
  }, [placementsByPosition])

  const baseUnpinnedPositions = useMemo(
    () => baseGroups.filter(g => !g.isPinned).map(g => g.position),
    [baseGroups]
  )

  const pinnedGroups = useMemo(
    () => baseGroups.filter(g => g.isPinned),
    [baseGroups]
  )

  const groupByPosition = useMemo(() => {
    const map = new Map<number, PositionGroup>()
    for (const g of baseGroups) map.set(g.position, g)
    return map
  }, [baseGroups])

  // Track in-flight view transition
  const transitionRef = useRef<{ finished: Promise<void> } | null>(null)

  // Drag state: tracks unpinned positions in order
  const [dragPosition, setDragPosition] = useState<number | null>(null)
  const [dragUnpinnedPositions, setDragUnpinnedPositions] = useState<number[] | null>(null)
  const handleActiveRef = useRef(false)

  /** Interleave unpinned positions with pinned ones to build display order */
  const buildDisplayGroups = useCallback((unpinnedPositions: number[]): PositionGroup[] => {
    const pinnedAtPos = new Map(pinnedGroups.map(g => [g.position, g]))
    const result: PositionGroup[] = []
    let uIdx = 0
    let pos = 0
    const maxPos = Math.max(
      unpinnedPositions.length + pinnedGroups.length,
      pinnedGroups.length > 0 ? Math.max(...pinnedGroups.map(g => g.position)) + 1 : 0,
    )

    while (pos < maxPos || uIdx < unpinnedPositions.length) {
      const pinned = pinnedAtPos.get(pos)
      if (pinned) {
        result.push({ ...pinned, position: pos })
      } else if (uIdx < unpinnedPositions.length) {
        const origPos = unpinnedPositions[uIdx]
        const origGroup = groupByPosition.get(origPos)
        if (origGroup) {
          result.push({ ...origGroup, position: pos })
        }
        uIdx++
      }
      pos++
    }
    return result
  }, [pinnedGroups, groupByPosition])

  const displayGroups = useMemo(() => {
    if (dragUnpinnedPositions) return buildDisplayGroups(dragUnpinnedPositions)
    return baseGroups
  }, [dragUnpinnedPositions, buildDisplayGroups, baseGroups])

  const nextPosition = baseGroups.length > 0
    ? Math.max(...baseGroups.map(g => g.position)) + 1
    : 0

  const handleDragStart = useCallback((e: React.DragEvent, pos: number) => {
    if (!handleActiveRef.current) {
      e.preventDefault()
      return
    }
    setDragPosition(pos)
    setDragUnpinnedPositions([...baseUnpinnedPositions])
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(pos))
  }, [baseUnpinnedPositions])

  const handleDragOver = useCallback((e: React.DragEvent, overGroupIdx: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dragPosition == null) return

    const update = () => setDragUnpinnedPositions(prev => {
      const order = prev || [...baseUnpinnedPositions]
      const oldIdx = order.indexOf(dragPosition)
      if (oldIdx === -1) return prev

      // Convert visual group index to unpinned index.
      // Count unpinned groups up to (but not including) overGroupIdx, then add 1
      // if the target group itself is unpinned — this gives us the 0-based
      // unpinned slot the user is hovering over (or the nearest one before a pin).
      let unpinnedBefore = 0
      for (let i = 0; i < overGroupIdx; i++) {
        const g = displayGroups[i]
        if (g && !g.isPinned) unpinnedBefore++
      }
      const overGroup = displayGroups[overGroupIdx]
      const targetIdx = overGroup && !overGroup.isPinned
        ? Math.min(unpinnedBefore, order.length - 1)
        : Math.max(0, unpinnedBefore - 1)

      if (oldIdx === targetIdx) return prev
      return arrayMove(order, oldIdx, targetIdx)
    })

    if (document.startViewTransition && !transitionRef.current) {
      const t = document.startViewTransition(() => flushSync(update))
      transitionRef.current = t
      t.finished.then(() => { transitionRef.current = null })
    } else if (!transitionRef.current) {
      update()
    }
  }, [dragPosition, baseUnpinnedPositions, displayGroups])

  const handleDragEnd = useCallback(() => {
    if (dragUnpinnedPositions && dragPosition != null) {
      const changed = dragUnpinnedPositions.some((pos, i) => pos !== baseUnpinnedPositions[i])
      if (changed) {
        // Build the new position assignments
        const newGroups = buildDisplayGroups(dragUnpinnedPositions)
        const updates: Array<{ placement_id: string; sort_order: number }> = []
        for (const group of newGroups) {
          for (const p of group.placements) {
            updates.push({ placement_id: p.id, sort_order: group.position })
          }
        }
        onReorder(region.id, updates)
      }
    }

    setDragPosition(null)
    setDragUnpinnedPositions(null)
    handleActiveRef.current = false
  }, [dragUnpinnedPositions, dragPosition, baseUnpinnedPositions, buildDisplayGroups, region.id, onReorder])

  return (
    <Panel key={region.id} padding="compact" margin="none">
      <Panel.Header>
        <h3>{region.name}</h3>
      </Panel.Header>

      {displayGroups.map((group, groupIdx) => {
        const isGrouped = group.placements.length > 1
        const isDragging = dragPosition != null && baseGroups.some(g => g.position === dragPosition && g.placements.some(p => group.placements.includes(p)))

        // Drag wrapper props — applied to the group container or single card
        const dragWrapperProps = !group.isPinned ? {
          draggable: true,
          onDragStart: (e: React.DragEvent) => handleDragStart(e, baseGroups.find(g => g.placements[0]?.id === group.placements[0]?.id)?.position ?? group.position),
          onDragOver: (e: React.DragEvent) => handleDragOver(e, groupIdx),
          onDrop: (e: React.DragEvent) => e.preventDefault(),
          onDragEnd: handleDragEnd,
        } : {
          onDragOver: (e: React.DragEvent) => handleDragOver(e, groupIdx),
          onDrop: (e: React.DragEvent) => e.preventDefault(),
        }

        if (isGrouped) {
          return (
            <div
              key={`group-${group.placements[0].id}`}
              className={`${rowStyles.positionGroup} ${isDragging ? rowStyles.draggingRow : ''} ${group.isPinned ? rowStyles.pinnedGroup : ''}`}
              style={{ viewTransitionName: `pos-${group.placements[0].id}` } as React.CSSProperties}
              {...dragWrapperProps}
            >
              <div className={rowStyles.groupHeader}>
                <span
                  className={rowStyles.dragHandle}
                  onMouseDown={!group.isPinned ? () => { handleActiveRef.current = true } : undefined}
                  onMouseUp={() => { handleActiveRef.current = false }}
                >
                  {!group.isPinned && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className={rowStyles.gripIcon}>
                      <circle cx="9" cy="6" r="1.5" /><circle cx="15" cy="6" r="1.5" />
                      <circle cx="9" cy="12" r="1.5" /><circle cx="15" cy="12" r="1.5" />
                      <circle cx="9" cy="18" r="1.5" /><circle cx="15" cy="18" r="1.5" />
                    </svg>
                  )}
                </span>
                <button
                  type="button"
                  className={`${rowStyles.pinButton} ${group.isPinned ? rowStyles.pinButtonActive : ''}`}
                  onClick={() => onPin(group.placements)}
                  title={group.isPinned ? `Pinned to position ${group.position + 1} — click to unpin` : 'Pin this position'}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                  </svg>
                </button>
                <span className={rowStyles.positionNumber}>{group.position + 1}</span>
                <span className={rowStyles.groupCount}>{group.placements.length} placements</span>
              </div>
              {group.placements.map(p => {
                const status = getPlacementStatus(p)
                return (
                  <div key={p.id} className={`${rowStyles.placementRow} ${rowStyles.groupedRow} ${!p.is_live ? rowStyles.draftRow : ''} ${p.is_deleted ? rowStyles.pendingRemovalRow : ''}`}>
                    <span>{/* grip spacer */}</span>
                    <span>{/* pin spacer */}</span>
                    <span>{/* pos# spacer */}</span>
                    <div className={rowStyles.placementNameCell}>
                      {!p.page_type && <span title="All Pages">🌐</span>}
                      <Link
                        to={placementPath(p.id)}
                        className={rowStyles.placementName}
                      >
                        {p.name}
                      </Link>
                      {p.slug && <span title="Targets a specific page"><Badge variant="neutral">{p.slug}</Badge></span>}
                    </div>
                    <span className={rowStyles.placementSchedule}>{getScheduleDisplay(p)}</span>
                    <span><WidgetTypeBadge code={p.widget.widget_type} /></span>
                    <span><Badge variant={status.variant}>{status.label}</Badge></span>
                    <KebabMenu
                      items={[
                        {
                          label: 'Schedule replacement',
                          onClick: () => {
                            const params = new URLSearchParams({
                              placement: 'new',
                              region_id: p.region.id,
                              sort_order: String(p.sort_order),
                            })
                            if (p.page_type) params.set('page_type', p.page_type)
                            if (p.page?.id) params.set('page_id', p.page.id)
                            if (p.slug) params.set('slug', p.slug)
                            if (p.ends_at) params.set('starts_at', convertUTCToPTInput(p.ends_at))
                            if (p.pinned_position != null) params.set('pinned_position', String(p.pinned_position))
                            navigate(placementPath('new', params))
                          },
                        },
                        { label: 'Duplicate', onClick: () => onDuplicate(p) },
                        { label: p.is_active ? 'Deactivate' : 'Activate', onClick: () => onToggleActive(p) },
                        { label: 'Delete placement', onClick: () => onDelete(p) },
                      ]}
                    />
                  </div>
                )
              })}
            </div>
          )
        }

        // Single placement — render as a card with drag handle inline
        const p = group.placements[0]
        const status = getPlacementStatus(p)
        const isPinned = p.pinned_position != null

        return (
          <div
            key={p.id}
            className={`${rowStyles.placementRow} ${isPinned ? rowStyles.pinnedRow : ''} ${isDragging ? rowStyles.draggingRow : ''} ${!p.is_live ? rowStyles.draftRow : ''} ${p.is_deleted ? rowStyles.pendingRemovalRow : ''}`}
            style={{ viewTransitionName: `pos-${p.id}` } as React.CSSProperties}
            {...dragWrapperProps}
          >
            <span
              className={rowStyles.dragHandle}
              onMouseDown={!isPinned ? () => { handleActiveRef.current = true } : undefined}
              onMouseUp={() => { handleActiveRef.current = false }}
            >
              {!isPinned && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className={rowStyles.gripIcon}>
                  <circle cx="9" cy="6" r="1.5" /><circle cx="15" cy="6" r="1.5" />
                  <circle cx="9" cy="12" r="1.5" /><circle cx="15" cy="12" r="1.5" />
                  <circle cx="9" cy="18" r="1.5" /><circle cx="15" cy="18" r="1.5" />
                </svg>
              )}
            </span>
            <button
              type="button"
              className={`${rowStyles.pinButton} ${isPinned ? rowStyles.pinButtonActive : ''}`}
              onClick={() => onPin([p])}
              title={isPinned ? `Pinned to position ${group.position + 1} — click to unpin` : 'Pin to this position'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
              </svg>
            </button>
            <span className={rowStyles.positionNumber}>{group.position + 1}</span>
            <div className={rowStyles.placementNameCell}>
              {!p.page_type && <span title="All Pages">🌐</span>}
              <Link
                to={placementPath(p.id)}
                className={rowStyles.placementName}
              >
                {p.name}
              </Link>
              {p.slug && <span title="Targets a specific page"><Badge variant="neutral">{p.slug}</Badge></span>}
            </div>
            <span className={rowStyles.placementSchedule}>{getScheduleDisplay(p)}</span>
            <span><WidgetTypeBadge code={p.widget.widget_type} /></span>
            <span><Badge variant={status.variant}>{status.label}</Badge></span>
            <KebabMenu
              items={[
                {
                  label: 'Schedule replacement',
                  onClick: () => {
                    const params = new URLSearchParams({
                      region_id: p.region.id,
                      sort_order: String(p.sort_order),
                    })
                    if (p.page_type) params.set('page_type', p.page_type)
                    if (p.page?.id) params.set('page_id', p.page.id)
                    if (p.slug) params.set('slug', p.slug)
                    if (p.ends_at) params.set('starts_at', convertUTCToPTInput(p.ends_at))
                    if (p.pinned_position != null) params.set('pinned_position', String(p.pinned_position))
                    navigate(placementPath('new', params))
                  },
                },
                { label: 'Duplicate', onClick: () => onDuplicate(p) },
                { label: p.is_active ? 'Deactivate' : 'Activate', onClick: () => onToggleActive(p) },
                { label: 'Delete', onClick: () => onDelete(p) },
              ]}
            />
          </div>
        )
      })}

      <div className={rowStyles.addRow}>
        <Link
          to={buildAddPlacementLink(region.id, nextPosition)}
          className={rowStyles.addPlacementLink}
        >
          + Add Placement
        </Link>
      </div>
    </Panel>
  )
}

export default RegionPanel
